import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ListVideo,
  FileText,
  BookOpen,
  Users,
  Settings,
  LogOut,
  Dumbbell,
  Trophy,
  Play,
  User,
  Shield,
  BarChart3,
  Video,
  Flag,
  Bell,
  Scale,
  Lock,
  Crown,
  Store,
  Menu,
  X,
  MessageCircle,
  LogIn
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import PremiumPromotionModal from './PremiumPromotionModal';
import { useTranslation } from 'react-i18next';
import { teamsAPI, notificationsAPI, messagesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { UserPlus, Loader2 } from 'lucide-react';
import NotificationDrawer from './NotificationDrawer';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { plan } = useTeam();
  const isPremium = plan === 'premium';
  const { t } = useTranslation();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // Join Team State
  const { toast } = useToast();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [teamCode, setTeamCode] = useState('');
  const [joining, setJoining] = useState(false);

  // Notification State
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (user) {
      const fetchUnreadCounts = async () => {
        try {
          const [notifRes, msgRes] = await Promise.all([
            notificationsAPI.getUnreadCount(),
            messagesAPI.getUnreadCount()
          ]);
          setUnreadNotifications(notifRes.data.unread_count);
          setUnreadMessages(msgRes.data.unread_count);
        } catch (error) {
          console.error('Failed to fetch unread counts:', error);
        }
      };
      fetchUnreadCounts();
      // Poll every 2 minutes
      const interval = setInterval(fetchUnreadCounts, 120000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleJoinTeam = async (e) => {
    e.preventDefault();
    if (!teamCode.trim()) return;

    setJoining(true);
    try {
      await teamsAPI.createJoinRequest(teamCode);
      toast({
        title: t('common.success'),
        description: t('nav.joinRequestSuccess'),
      });
      setShowJoinModal(false);
      setTeamCode('');
    } catch (error) {
      console.log(error);

      let errorMessage = t('nav.joinRequestError');
      try {
        const detail = error.response?.data?.detail;

        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => String(err?.msg || 'Validation error')).join(', ');
        } else if (detail && typeof detail === 'object') {
          errorMessage = detail.message || JSON.stringify(detail);
        }
      } catch (e) {
        console.error("Error parsing error message", e);
      }

      toast({
        variant: "destructive",
        title: t('common.error'),
        description: String(errorMessage),
      });
    } finally {
      setJoining(false);
    }
  };

  const baseNavItems = [
    { path: '/feed', icon: Play, label: t('nav.social') },
    { path: '/league', icon: Trophy, label: t('nav.league') },
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/notes', icon: FileText, label: t('nav.announcements') },
    { path: '/playbook', icon: BookOpen, label: t('nav.playbook') },
    { path: '/training', icon: Dumbbell, label: t('nav.training') },
    { path: '/videos', icon: Video, label: t('nav.videos') },
    { path: '/roster', icon: Users, label: t('nav.roster') },
    { path: '/stats', icon: BarChart3, label: t('nav.stats'), premiumOnly: true },
  ];

  const coachOnlyItems = [
    { path: '/analytics', icon: LayoutDashboard, label: t('nav.teamManagement') },
  ];

  const adminOnlyItems = [
    { path: '/admin', icon: Shield, label: t('nav.admin') },
  ];

  // Build navigation items based on auth state
  let navItems = [];

  const marketItem = { path: '/market', icon: Store, label: t('nav.market') };

  if (!user) {
    // Guest user - show only public pages
    const publicItems = baseNavItems.filter(item => ['/feed', '/league', '/stats'].includes(item.path));
    // Remove premiumOnly restriction for public stats
    const publicItemsClean = publicItems.map(item => ({ ...item, premiumOnly: false }));
    navItems = [
      ...publicItemsClean,
      { type: 'divider' },
      marketItem,
    ];
  } else {
    const profileItem = { path: `/profile/${user?.username}`, icon: User, label: t('nav.profile') };
    const settingsItem = { path: '/settings', icon: Settings, label: t('nav.settings') };

    if (!user?.team_id) {
      // Logged in but no team
      const standardItems = baseNavItems.filter(item => ['/feed', '/league'].includes(item.path));
      navItems = [
        ...standardItems,
        { type: 'divider' },
        marketItem,
        { type: 'divider' },
        profileItem,
        settingsItem
      ];
    } else {
      // Team user (Full menu)
      const leagueIndex = baseNavItems.findIndex(item => item.path === '/league');
      const beforeLeague = baseNavItems.slice(0, leagueIndex + 1);
      const afterLeague = baseNavItems.slice(leagueIndex + 1);

      navItems = [
        ...beforeLeague,
        { type: 'divider' },
        ...afterLeague,
        ...(user?.role === 'coach' || user?.role === 'admin' ? coachOnlyItems : []),
        { type: 'divider' },
        marketItem,
        { type: 'divider' },
        profileItem,
        settingsItem,
        ...(user?.role === 'admin' ? adminOnlyItems : [])
      ];
    }
  }

  return (
    <>
      {/* Sidebar - Hidden on mobile, visible on desktop */}
      <div
        className="hidden lg:flex w-64 bg-[#0a0a0b] border-r border-gray-800 flex-col h-screen relative z-40"
      >
        {/* Logo Section */}
        <div className="p-4 pl-6 border-b border-gray-800 relative flex items-center justify-start min-h-[90px]">
          <Link to="/feed" className="flex items-center">
            <img
              src="/snapp-logo-new.png"
              alt="Snapp Logo"
              className="h-14 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 overflow-y-auto scrollbar-hide">
          {navItems.map((item, index) => {
            if (item.type === 'divider') {
              return <hr key={`divider-${index}`} className="border-gray-800 my-2 mx-4" />;
            }

            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.premiumOnly && !isPremium ? '#' : item.path}
                onClick={(e) => {
                  if (item.premiumOnly && !isPremium) {
                    e.preventDefault();
                    setShowPremiumModal(true);
                    return;
                  }
                }}
                className={`flex items-center px-6 py-3 text-sm font-medium transition-all duration-200 ${isActive
                  ? 'text-white bg-gradient-to-r from-snapp-green/20 to-transparent border-l-4 border-snapp-green shadow-sm'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50 hover:border-l-4 hover:border-snapp-green/30'
                  } ${item.premiumOnly && !isPremium ? 'opacity-80' : ''}`}
              >
                <div className="flex items-center flex-1">
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                </div>
                {item.showBadge && unreadMessages > 0 && (
                  <span className="bg-snapp-green text-[#0a0a0b] text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center border-2 border-[#0a0a0b] ml-2">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
                {item.premiumOnly && !isPremium && (
                  <Lock className="w-3.5 h-3.5 text-amber-500/50" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="px-4 py-3 border-t border-gray-800">
          {user ? (
            <>
              {/* Team Profile or Join Team */}
              {user?.team_id ? (
                <Link
                  to={`/teams/${user.team_id}/profile`}
                  className={`flex items-center px-6 py-3 text-sm font-medium transition-all duration-200 mb-2 rounded-lg ${location.pathname.includes(`/teams/${user.team_id}/profile`)
                    ? 'text-white bg-gradient-to-r from-snapp-purple/30 to-snapp-blue/20 border-l-4 border-snapp-purple'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                >
                  {user?.team_logo_url ? (
                    <img src={user.team_logo_url} alt={user.team_name} className="w-5 h-5 mr-3 object-contain" />
                  ) : (
                    <Flag className="w-5 h-5 mr-3" />
                  )}
                  {user?.team_name}
                </Link>
              ) : user?.membership_type !== 'seller' ? (
                <button
                  onClick={() => setShowJoinModal(true)}
                  className="flex items-center w-full px-6 py-3 text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors mb-2"
                >
                  <UserPlus className="w-5 h-5 mr-3" />
                  {t('nav.joinTeam')}
                </button>
              ) : null}

              {/* User info & actions */}
              <div className="flex items-center justify-between mt-4">
                <div>
                  <p className="text-sm font-medium text-white">{user?.username}</p>
                  {user?.membership_type === 'seller' ? (
                    <p className="text-xs text-emerald-400 font-semibold">Seller</p>
                  ) : (
                    <p className="text-xs text-gray-400 capitalize">{t(`common.${user?.role}`)}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Link
                    to="/messages"
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors relative"
                    title={t('nav.messages')}
                  >
                    <MessageCircle className="w-5 h-5" />
                    {unreadMessages > 0 && (
                      <span className="absolute top-1 right-1 w-4 h-4 bg-snapp-green text-[#0a0a0b] text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-[#0a0a0b]">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={() => setIsNotificationDrawerOpen(true)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors relative"
                    title={t('nav.notifications')}
                  >
                    <Bell className="w-5 h-5" />
                    {unreadNotifications > 0 && (
                      <span className="absolute top-1 right-1 w-4 h-4 bg-snapp-green text-[#0a0a0b] text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-[#0a0a0b]">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={logout}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    title={t('nav.logout')}
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* Guest - Login/Sign Up buttons */
            <div className="flex flex-col gap-2 py-2">
              <button
                onClick={() => navigate('/login')}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-snapp-green text-[#0a0a0b] font-semibold rounded-lg hover:bg-snapp-green/90 transition-all duration-200 text-sm"
              >
                <LogIn className="w-4 h-4" />
                Giriş Yap
              </button>
              <button
                onClick={() => navigate('/login')}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 border border-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-800 hover:text-white transition-all duration-200 text-sm"
              >
                <UserPlus className="w-4 h-4" />
                Kayıt Ol
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Join Team Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-[#1a1a1b] border border-gray-800 rounded-lg p-6 w-full max-w-sm relative">
            <button
              onClick={() => setShowJoinModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-xl font-bold text-white mb-2">{t('nav.joinTeamTitle')}</h3>
            <p className="text-gray-400 text-sm mb-6">{t('nav.joinTeamDesc')}</p>

            <form onSubmit={handleJoinTeam}>
              <input
                type="text"
                placeholder={t('nav.teamCodePlaceholder')}
                value={teamCode}
                onChange={(e) => setTeamCode(e.target.value.toUpperCase())}
                className="w-full bg-[#0a0a0b] border border-gray-700 rounded p-3 text-white mb-4 uppercase tracking-widest text-center font-mono"
                autoFocus
              />
              <button
                type="submit"
                disabled={joining || !teamCode.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center"
              >
                {joining ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t('nav.joinTeam')
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Notification Drawer */}
      <NotificationDrawer
        isOpen={isNotificationDrawerOpen}
        onClose={() => setIsNotificationDrawerOpen(false)}
        onUnreadUpdate={setUnreadNotifications}
      />

      <PremiumPromotionModal
        open={showPremiumModal}
        onOpenChange={setShowPremiumModal}
      />
    </>
  );
};

export default Sidebar;
